import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  MapPin, ZoomIn, ZoomOut, CheckSquare, Square, Maximize2,
  Pencil, Trash2, Plus, X, Move, MousePointer,
} from "lucide-react";

const ROOM_COLORS = [
  { bg: "rgba(0, 102, 255, 0.22)", border: "rgba(0, 102, 255, 0.6)", text: "#0052CC" },
  { bg: "rgba(16, 185, 129, 0.22)", border: "rgba(16, 185, 129, 0.6)", text: "#047857" },
  { bg: "rgba(245, 158, 11, 0.22)", border: "rgba(245, 158, 11, 0.6)", text: "#B45309" },
  { bg: "rgba(139, 92, 246, 0.22)", border: "rgba(139, 92, 246, 0.6)", text: "#6D28D9" },
  { bg: "rgba(236, 72, 153, 0.22)", border: "rgba(236, 72, 153, 0.6)", text: "#BE185D" },
  { bg: "rgba(6, 182, 212, 0.22)", border: "rgba(6, 182, 212, 0.6)", text: "#0E7490" },
  { bg: "rgba(249, 115, 22, 0.22)", border: "rgba(249, 115, 22, 0.6)", text: "#C2410C" },
  { bg: "rgba(34, 197, 94, 0.22)", border: "rgba(34, 197, 94, 0.6)", text: "#15803D" },
];

const SELECTED_OVERLAY = "rgba(0, 102, 255, 0.30)";
const SELECTED_BORDER = "rgba(0, 102, 255, 0.85)";
const HANDLE_SIZE = 8;

export default function RoomSelector({ blueprintPreview, aiRooms, onRoomSelect, selectedRooms, onRoomsChange }) {
  const [zoom, setZoom] = useState(1);
  const [editMode, setEditMode] = useState(false);
  const [drawMode, setDrawMode] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);

  // Resize state
  const [resizing, setResizing] = useState(null); // { roomIndex, handle, startPos, startBbox }

  // Draw state
  const [drawing, setDrawing] = useState(null); // { startX, startY, currentX, currentY }

  // Editing room name
  const [editingNameIdx, setEditingNameIdx] = useState(null);
  const [editingNameVal, setEditingNameVal] = useState("");

  const rooms = useMemo(() => aiRooms || [], [aiRooms]);
  const allSelected = rooms.length > 0 && selectedRooms && selectedRooms.length === rooms.length;
  const hasBboxData = rooms.some((r) => r.bbox);

  // Coordinate helper: mouse event -> image-relative % position
  const getImagePos = (e) => {
    const img = imgRef.current;
    if (!img) return null;
    const rect = img.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100)),
    };
  };

  // --- Selection handlers ---
  const handleSelectAll = () => {
    if (allSelected) {
      onRoomSelect([], []);
    } else {
      onRoomSelect(rooms.map((_, i) => i), rooms);
    }
  };

  const handleToggleRoom = (index) => {
    if (editMode && !drawMode) return; // In edit mode, clicks are for resize/move
    const current = selectedRooms || [];
    const next = current.includes(index) ? current.filter((i) => i !== index) : [...current, index];
    onRoomSelect(next, rooms.filter((_, i) => next.includes(i)));
  };

  // --- Edit handlers ---
  const handleDeleteRoom = (index) => {
    const newRooms = rooms.filter((_, i) => i !== index);
    if (onRoomsChange) onRoomsChange(newRooms);
    // Update selection indices (shift down indices above deleted)
    const newSelected = (selectedRooms || [])
      .filter((i) => i !== index)
      .map((i) => (i > index ? i - 1 : i));
    onRoomSelect(newSelected, newRooms.filter((_, i) => newSelected.includes(i)));
  };

  const handleRoomM2Change = (index, value) => {
    const newRooms = rooms.map((r, i) => (i === index ? { ...r, estimated_m2: value } : r));
    if (onRoomsChange) onRoomsChange(newRooms);
    // Re-trigger selection update to recalculate total m2
    const current = selectedRooms || [];
    if (current.includes(index)) {
      onRoomSelect(current, newRooms.filter((_, i) => current.includes(i)));
    }
  };

  const handleNameEditStart = (index) => {
    setEditingNameIdx(index);
    setEditingNameVal(rooms[index]?.name || rooms[index]?.type || "");
  };

  const handleNameEditSave = (index) => {
    if (editingNameVal.trim()) {
      const newRooms = rooms.map((r, i) => (i === index ? { ...r, name: editingNameVal.trim() } : r));
      if (onRoomsChange) onRoomsChange(newRooms);
    }
    setEditingNameIdx(null);
  };

  // --- Resize handlers ---
  const handleResizeStart = (e, roomIndex, handle) => {
    e.stopPropagation();
    e.preventDefault();
    const pos = getImagePos(e);
    if (!pos) return;
    setResizing({ roomIndex, handle, startPos: pos, startBbox: { ...rooms[roomIndex].bbox } });
  };

  const handleResizeMove = useCallback((e) => {
    if (!resizing) return;
    const pos = getImagePos(e);
    if (!pos) return;
    const { roomIndex, handle, startPos, startBbox } = resizing;
    const dx = pos.x - startPos.x;
    const dy = pos.y - startPos.y;

    let newBbox = { ...startBbox };
    if (handle.includes("n")) { newBbox.y = startBbox.y + dy; newBbox.h = startBbox.h - dy; }
    if (handle.includes("s")) { newBbox.h = startBbox.h + dy; }
    if (handle.includes("w")) { newBbox.x = startBbox.x + dx; newBbox.w = startBbox.w - dx; }
    if (handle.includes("e")) { newBbox.w = startBbox.w + dx; }

    // Clamp min size 2%
    if (newBbox.w < 2) { newBbox.w = 2; if (handle.includes("w")) newBbox.x = startBbox.x + startBbox.w - 2; }
    if (newBbox.h < 2) { newBbox.h = 2; if (handle.includes("n")) newBbox.y = startBbox.y + startBbox.h - 2; }
    newBbox.x = Math.max(0, Math.min(100 - newBbox.w, newBbox.x));
    newBbox.y = Math.max(0, Math.min(100 - newBbox.h, newBbox.y));

    const newRooms = rooms.map((r, i) => (i === roomIndex ? { ...r, bbox: newBbox } : r));
    if (onRoomsChange) onRoomsChange(newRooms);
  }, [resizing, rooms, onRoomsChange]);

  const handleResizeEnd = useCallback(() => {
    setResizing(null);
  }, []);

  // --- Draw handlers ---
  const handleDrawStart = (e) => {
    if (!drawMode) return;
    const pos = getImagePos(e);
    if (!pos) return;
    e.preventDefault();
    setDrawing({ startX: pos.x, startY: pos.y, currentX: pos.x, currentY: pos.y });
  };

  const handleDrawMove = useCallback((e) => {
    if (!drawing) return;
    const pos = getImagePos(e);
    if (!pos) return;
    setDrawing((prev) => ({ ...prev, currentX: pos.x, currentY: pos.y }));
  }, [drawing]);

  const handleDrawEnd = useCallback(() => {
    if (!drawing) return;
    const { startX, startY, currentX, currentY } = drawing;
    const x = Math.min(startX, currentX);
    const y = Math.min(startY, currentY);
    const w = Math.abs(currentX - startX);
    const h = Math.abs(currentY - startY);

    if (w >= 2 && h >= 2) {
      const newRoom = {
        name: `Estancia ${rooms.length + 1}`,
        type: "Manual",
        estimated_m2: "",
        bbox: { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10, w: Math.round(w * 10) / 10, h: Math.round(h * 10) / 10 },
        manual: true,
      };
      const newRooms = [...rooms, newRoom];
      if (onRoomsChange) onRoomsChange(newRooms);
      // Auto-select the new room
      const newIdx = newRooms.length - 1;
      const newSelected = [...(selectedRooms || []), newIdx];
      onRoomSelect(newSelected, newRooms.filter((_, i) => newSelected.includes(i)));
    }

    setDrawing(null);
    setDrawMode(false);
  }, [drawing, rooms, selectedRooms, onRoomsChange, onRoomSelect]);

  // Attach/detach global mousemove/mouseup for resize and draw
  useEffect(() => {
    if (resizing) {
      const onMove = (e) => handleResizeMove(e);
      const onUp = () => handleResizeEnd();
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    }
  }, [resizing, handleResizeMove, handleResizeEnd]);

  useEffect(() => {
    if (drawing) {
      const onMove = (e) => handleDrawMove(e);
      const onUp = () => handleDrawEnd();
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    }
  }, [drawing, handleDrawMove, handleDrawEnd]);

  // Drag-to-pan (only when NOT in edit/draw mode and zoom > 1)
  const handleMouseDown = (e) => {
    if (drawMode) { handleDrawStart(e); return; }
    if (editMode || zoom <= 1) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    if (containerRef.current) setScrollStart({ x: containerRef.current.scrollLeft, y: containerRef.current.scrollTop });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !containerRef.current) return;
    containerRef.current.scrollLeft = scrollStart.x - (e.clientX - dragStart.x);
    containerRef.current.scrollTop = scrollStart.y - (e.clientY - dragStart.y);
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleZoomIn = () => setZoom((z) => Math.min(3, z + 0.25));
  const handleZoomOut = () => setZoom((z) => Math.max(0.5, z - 0.25));
  const handleFitToView = () => setZoom(1);

  // Draw preview rect
  const drawRect = drawing ? {
    x: Math.min(drawing.startX, drawing.currentX),
    y: Math.min(drawing.startY, drawing.currentY),
    w: Math.abs(drawing.currentX - drawing.startX),
    h: Math.abs(drawing.currentY - drawing.startY),
  } : null;

  // Resize handles for a room
  const renderHandles = (roomIdx) => {
    const handlePositions = [
      { key: "nw", style: { top: -HANDLE_SIZE / 2, left: -HANDLE_SIZE / 2, cursor: "nwse-resize" } },
      { key: "ne", style: { top: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2, cursor: "nesw-resize" } },
      { key: "sw", style: { bottom: -HANDLE_SIZE / 2, left: -HANDLE_SIZE / 2, cursor: "nesw-resize" } },
      { key: "se", style: { bottom: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2, cursor: "nwse-resize" } },
    ];
    return handlePositions.map(({ key, style }) => (
      <div
        key={key}
        onMouseDown={(e) => handleResizeStart(e, roomIdx, key)}
        style={{
          position: "absolute", ...style,
          width: HANDLE_SIZE, height: HANDLE_SIZE,
          backgroundColor: "#0066FF", border: "1.5px solid white",
          borderRadius: 2, zIndex: 10,
        }}
        data-testid={`resize-handle-${roomIdx}-${key}`}
      />
    ));
  };

  // Early return AFTER all hooks are defined
  if (!blueprintPreview) return null;

  return (
    <Card className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm mt-4">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-base font-semibold text-[#1B2B4D] flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[#0066FF]" />
            Selección de Estancias
          </CardTitle>
          {hasBboxData && onRoomsChange && (
            <Button
              variant={editMode ? "default" : "outline"}
              size="sm"
              onClick={() => { setEditMode(!editMode); setDrawMode(false); setDrawing(null); }}
              className={editMode ? "bg-[#0066FF] text-white h-7 text-xs" : "h-7 text-xs border-[#E2E8F0] text-[#4A5568]"}
              data-testid="edit-mode-toggle"
            >
              <Pencil className="h-3 w-3 mr-1" />
              {editMode ? "Salir de edición" : "Editar estancias"}
            </Button>
          )}
        </div>
        <p className="text-xs text-[#4A5568]">
          {drawMode
            ? "Haz clic y arrastra sobre el plano para dibujar una nueva estancia"
            : editMode
              ? "Arrastra las esquinas para redimensionar. Haz clic en X para eliminar."
              : hasBboxData
                ? "Haz clic en las estancias del plano o en los botones para seleccionarlas"
                : "Selecciona las estancias para las que quieres calcular materiales"}
        </p>
      </CardHeader>
      <CardContent>
        {/* Blueprint image viewer with overlays */}
        <div className="relative bg-[#F7FAFC] rounded-lg border border-[#E2E8F0] overflow-hidden mb-3">
          {/* Toolbar */}
          <div className="flex items-center justify-between gap-1 px-3 py-1.5 bg-white/90 border-b border-[#E2E8F0] z-10 relative">
            <div className="flex items-center gap-2">
              {editMode && (
                <Button
                  variant={drawMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => setDrawMode(!drawMode)}
                  className={drawMode ? "bg-green-600 hover:bg-green-700 text-white h-7 text-xs" : "h-7 text-xs border-[#E2E8F0] text-[#4A5568]"}
                  data-testid="draw-room-btn"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {drawMode ? "Dibujando..." : "Agregar estancia"}
                </Button>
              )}
              <span className="text-[10px] text-[#4A5568]">
                {drawMode ? "Arrastra para dibujar" : zoom > 1 ? "Arrastra para mover" : "Amplia para navegar"}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={handleFitToView} className="h-7 w-7 p-0" data-testid="fit-view-btn" title="Ajustar a vista">
                <Maximize2 className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleZoomOut} className="h-7 w-7 p-0" data-testid="zoom-out-btn">
                <ZoomOut className="h-3.5 w-3.5" />
              </Button>
              <span className="text-xs text-[#4A5568] min-w-[40px] text-center font-medium">{Math.round(zoom * 100)}%</span>
              <Button variant="ghost" size="sm" onClick={handleZoomIn} className="h-7 w-7 p-0" data-testid="zoom-in-btn">
                <ZoomIn className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Image container */}
          <div
            ref={containerRef}
            className="overflow-auto"
            style={{
              maxHeight: "60vh",
              cursor: drawMode ? "crosshair" : isDragging ? "grabbing" : zoom > 1 ? "grab" : "default",
              userSelect: "none",
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            data-testid="blueprint-container"
          >
            <div className="relative inline-block" style={{ transform: `scale(${zoom})`, transformOrigin: "top left", minWidth: "100%" }}>
              <img
                ref={imgRef}
                src={blueprintPreview}
                alt="Plano"
                className="block w-full h-auto"
                style={{ objectFit: "contain" }}
                onLoad={() => setImgLoaded(true)}
                draggable={false}
                data-testid="blueprint-image-zoomed"
              />

              {/* Room overlays */}
              {imgLoaded && hasBboxData && rooms.map((room, i) => {
                if (!room.bbox) return null;
                const isSelected = selectedRooms && selectedRooms.includes(i);
                const color = ROOM_COLORS[i % ROOM_COLORS.length];
                const label = room.name || room.type || `Estancia ${i + 1}`;

                return (
                  <div
                    key={i}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!editMode) handleToggleRoom(i);
                    }}
                    className="absolute transition-colors duration-150"
                    style={{
                      left: `${room.bbox.x}%`,
                      top: `${room.bbox.y}%`,
                      width: `${room.bbox.w}%`,
                      height: `${room.bbox.h}%`,
                      backgroundColor: isSelected ? SELECTED_OVERLAY : "rgba(200,200,200,0.08)",
                      border: isSelected ? `2.5px solid ${SELECTED_BORDER}` : `1.5px dashed ${color.border}`,
                      borderRadius: 4,
                      cursor: editMode ? "default" : "pointer",
                      zIndex: isSelected ? 3 : 2,
                    }}
                    data-testid={`room-overlay-${i}`}
                    title={`${label}${room.estimated_m2 ? ` (${room.estimated_m2} m2)` : ""}`}
                  >
                    {/* Room label */}
                    <div
                      className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-bold leading-tight whitespace-nowrap"
                      style={{
                        backgroundColor: isSelected ? "rgba(0,102,255,0.85)" : "rgba(255,255,255,0.92)",
                        color: isSelected ? "#fff" : color.text,
                        border: isSelected ? "none" : `1px solid ${color.border}`,
                        pointerEvents: "none",
                      }}
                    >
                      {label}
                      {room.estimated_m2 ? ` · ${room.estimated_m2}m2` : ""}
                    </div>

                    {/* Selected checkmark (view mode) */}
                    {isSelected && !editMode && (
                      <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#0066FF] flex items-center justify-center" style={{ pointerEvents: "none" }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                    )}

                    {/* Delete button (edit mode) */}
                    {editMode && !drawMode && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteRoom(i); }}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center shadow-sm"
                        style={{ zIndex: 10, cursor: "pointer" }}
                        data-testid={`delete-room-${i}`}
                        title="Eliminar estancia"
                      >
                        <X className="h-3 w-3 text-white" strokeWidth={3} />
                      </button>
                    )}

                    {/* Resize handles (edit mode) */}
                    {editMode && !drawMode && renderHandles(i)}
                  </div>
                );
              })}

              {/* Drawing preview */}
              {drawRect && drawRect.w > 0 && drawRect.h > 0 && (
                <div
                  className="absolute border-2 border-dashed border-green-500 bg-green-500/15 rounded"
                  style={{
                    left: `${drawRect.x}%`,
                    top: `${drawRect.y}%`,
                    width: `${drawRect.w}%`,
                    height: `${drawRect.h}%`,
                    pointerEvents: "none",
                    zIndex: 5,
                  }}
                  data-testid="draw-preview"
                />
              )}
            </div>
          </div>
        </div>

        {/* Room badges */}
        {rooms.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-[#1B2B4D]">
                {editMode ? "Estancias (modo edición):" : "Estancias detectadas por IA:"}
              </p>
              {!editMode && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectAll}
                  className="h-7 text-xs text-[#0066FF] hover:bg-[#E3F2FD] px-2"
                  data-testid="select-all-rooms-btn"
                >
                  {allSelected ? <CheckSquare className="h-3.5 w-3.5 mr-1" /> : <Square className="h-3.5 w-3.5 mr-1" />}
                  {allSelected ? "Deseleccionar todo" : "Seleccionar todo"}
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {rooms.map((room, i) => {
                const label = room.name || room.type || `Estancia ${i + 1}`;
                const isSelected = selectedRooms && selectedRooms.includes(i);
                const color = ROOM_COLORS[i % ROOM_COLORS.length];

                if (editMode) {
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-[#E2E8F0] bg-white"
                      data-testid={`room-edit-badge-${i}`}
                    >
                      <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color.border }} />
                      {editingNameIdx === i ? (
                        <Input
                          value={editingNameVal}
                          onChange={(e) => setEditingNameVal(e.target.value)}
                          onBlur={() => handleNameEditSave(i)}
                          onKeyDown={(e) => { if (e.key === "Enter") handleNameEditSave(i); }}
                          className="h-6 w-24 text-xs px-1 border-[#CBD5E0]"
                          autoFocus
                          data-testid={`room-name-input-${i}`}
                        />
                      ) : (
                        <span
                          className="font-medium text-[#1B2B4D] cursor-pointer hover:text-[#0066FF]"
                          onClick={() => handleNameEditStart(i)}
                          title="Haz clic para editar el nombre"
                          data-testid={`room-name-${i}`}
                        >
                          {label}
                        </span>
                      )}
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="m²"
                        value={room.estimated_m2 || ""}
                        onChange={(e) => handleRoomM2Change(i, e.target.value)}
                        className="h-6 w-16 text-xs px-1 border-[#CBD5E0]"
                        data-testid={`room-m2-input-${i}`}
                      />
                      <span className="text-[10px] text-[#4A5568]">m²</span>
                      <button
                        onClick={() => handleDeleteRoom(i)}
                        className="ml-1 text-red-400 hover:text-red-600"
                        data-testid={`room-badge-delete-${i}`}
                        title="Eliminar"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                }

                return (
                  <button
                    key={i}
                    onClick={() => handleToggleRoom(i)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${
                      isSelected
                        ? "bg-[#0066FF] text-white border-[#0066FF] shadow-sm"
                        : "bg-white text-[#1B2B4D] border-[#E2E8F0] hover:border-[#0066FF] hover:bg-[#E3F2FD]"
                    }`}
                    data-testid={`room-select-${i}`}
                  >
                    {isSelected ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full mr-0.5"
                      style={{ backgroundColor: isSelected ? "white" : color.border }}
                    />
                    <span>{label}</span>
                    {room.estimated_m2 && (
                      <Badge className={`ml-1 text-[10px] ${isSelected ? "bg-white/20 text-white border-white/30" : "bg-[#F0F4F8] text-[#4A5568] border-[#E2E8F0]"}`}>
                        {room.estimated_m2} m2
                      </Badge>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Selection summary */}
            {!editMode && selectedRooms && selectedRooms.length > 0 && (
              <div className="mt-3 p-2 bg-[#E3F2FD] rounded-lg" data-testid="selected-rooms-summary">
                <p className="text-xs font-semibold text-[#0066FF]">
                  {selectedRooms.length} estancia{selectedRooms.length > 1 ? "s" : ""} seleccionada{selectedRooms.length > 1 ? "s" : ""}
                  {(() => {
                    const totalM2 = selectedRooms.reduce((sum, idx) => {
                      const room = rooms[idx];
                      return sum + (room?.estimated_m2 ? parseFloat(room.estimated_m2) : 0);
                    }, 0);
                    return totalM2 > 0 ? ` | Total: ${totalM2.toFixed(2)} m²` : "";
                  })()}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Fallback when no rooms */}
        {rooms.length === 0 && (
          <div className="text-center py-3">
            <p className="text-xs text-[#4A5568] italic mb-2">
              {editMode ? "No hay estancias. Dibuja una nueva." : "La IA no detectó múltiples estancias. Puedes introducir los datos manualmente."}
            </p>
            {onRoomsChange && !editMode && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setEditMode(true); setDrawMode(true); }}
                className="text-xs h-7 border-[#E2E8F0] text-[#4A5568]"
                data-testid="start-draw-empty-btn"
              >
                <Plus className="h-3 w-3 mr-1" /> Dibujar estancias manualmente
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
